import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useGlobalActions, useGlobalStore } from '../../../global-store'
import { getEvents } from '../../api/events'
import { CurrentEventType } from '../../common-types'
import BackToTop from '../../components/back-to-top'
import ButtonPrimary from '../../components/button-primary'
import ButtonSecondary from '../../components/button-secondary'
import Container from '../../components/container'
import DefaultResponse from '../../components/default-response'
import EventFilters from '../../components/event-filters'
import FullscreenLoader from '../../components/fullscreen-loader'
import LoadMoreSection from '../../components/load-more-section'
import PageHeader from '../../components/page-header'
import PricingView from '../../components/pricing-view'
import VerticalDrawer from '../../components/vertical-drawer'
import { PAGE_SIZE } from '../../config'
import { colors, constants } from '../../tokens'
import {
  SCancelledOverlay,
  SCardActions,
  SCardContent,
  SCardMedia,
  SDateContainerDesktop,
  SDateContainerMobile,
  SDateDayDisplay,
  SDateText,
  SEventItem,
  SEventsSection,
  SEventWrapper,
  SItem,
  SItemDetail,
  SItemDetailHeader,
  STimeAndCommentContainer,
  SVenueImageContainer,
} from './styles/events.styled'
import { EventComment } from './components/comment-box'
import { getVenueLogo } from '../../utils'
import GenericError from '../../components/generic-error'

const EventDateHeader = ({
  index,
  item,
  currentEventsData,
}: {
  index: number
  item: any
  currentEventsData: any
}) => {
  if (index === 0) {
    return (
      <PageHeader
        title={`${dayjs(item.EventDateTime).format('MMMM YYYY')}`}
        style={{ paddingBottom: 0, paddingTop: 0 }}
      />
    )
  }
  if (
    dayjs(currentEventsData[index - 1].EventDateTime).get('month') !==
    dayjs(item.EventDateTime).get('month')
  ) {
    return <PageHeader title={`${dayjs(item.EventDateTime).format('MMMM YYYY')}`} />
  }
  return <></>
}

function EventName({ eventName = '' }) {
  const eventNameSeparator = eventName?.includes(' - ') ? ' - ' : ':'
  const eventNameArray = eventName.split(eventNameSeparator)
  const eventIsSports =
    eventName?.toLowerCase()?.includes('knicks') || eventName?.toLowerCase()?.includes('rangers')
  return (
    <SItemDetailHeader>
      {eventIsSports ? (
        <div>
          <strong>{eventName}</strong>
        </div>
      ) : (
        <>
          <div>
            <strong>{eventNameArray[0]}</strong>
          </div>
          <div style={{ fontSize: '0.8em', lineHeight: 2, opacity: 0.75 }}>
            {eventNameArray[1] ? ` ${eventNameArray[1]}` : ''}
          </div>
        </>
      )}
    </SItemDetailHeader>
  )
}

export default function Events() {
  // pagination control for loading events
  const [dataPagesLoaded, setDataPagesLoaded] = useState(1)
  const [dataPageLoadError, setDataPageLoadError] = useState(false)
  const [isLoadingMoreEvents, setIsLoadingMore] = useState(false)
  const [currentEventId, setCurrentEventId] = useState('')
  const { currentEvents, eventFilters, userInfo } = useGlobalStore()
  const { setCurrentEvents } = useGlobalActions()
  const router = useRouter()

  const onLoadMore = () => {
    setDataPagesLoaded((oldVal) => oldVal + 1)
  }

  useEffect(() => {
    if (dataPagesLoaded > 1) {
      setDataPageLoadError(false)
      if (!currentEvents.isLoading && currentEvents.data) {
        setIsLoadingMore(true)
        const filters = eventFilters.data
          ? {
              date: eventFilters.data.date || '',
              eventName: eventFilters.data.eventName || '',
              venueName: eventFilters.data.venueName || '',
            }
          : {}
        getEvents({ page: dataPagesLoaded, ...filters })
          .then((data) => {
            if (data?.EventsByRequest) {
              const currentTimestamp = dayjs().valueOf()
              data.EventsByRequest = data?.EventsByRequest.filter((event: any) => {
                const eventTimestamp = dayjs(event.EventDateTime).valueOf() // Convert event time to timestamp
                return eventTimestamp >= currentTimestamp
              })
            }
            setIsLoadingMore(false)
            !!data &&
              setCurrentEvents({
                data: [...(currentEvents.data ? currentEvents.data : []), ...data?.EventsByRequest],
                totalCount: data.TotalCount,
              })
          })
          .catch(() => {
            setDataPageLoadError(true)
            setIsLoadingMore(false)
          })
      }
    }
  }, [dataPagesLoaded])

  useEffect(() => {
    if (eventFilters && eventFilters.data) {
      setDataPagesLoaded(1)
    }
  }, [eventFilters])

  return (
    <>
      <Head>
        <title>Madison Square Garden -- Request Tix -- Events List</title>
      </Head>
      <Container>
        <FullscreenLoader show={Boolean(currentEvents.isLoading) || Boolean(userInfo.isLoading)} />
        <EventFilters />
        <SEventsSection>
          {!!currentEvents.data && !!currentEvents.data.length ? (
            <Stack spacing={2}>
              {currentEvents.data.map((item: CurrentEventType, index: number) => (
                <div key={`${item.EventId}-${index}`}>
                  <EventDateHeader index={index} currentEventsData={currentEvents.data} item={item} />
                  <SEventWrapper>
                    <SEventItem
                      sx={{
                        background: colors.globalDarkBlueTransparent,
                        color: 'white',
                      }}
                    >
                      <DateContainer mode="desktop" dateTime={item.EventDateTime} />
                      <SCardMedia image={item['ImageUrl360X640']} title={item.EventName}>
                        {item.Status === 'cancelled' ? <SCancelledOverlay /> : <></>}
                      </SCardMedia>
                      <DateContainer mode="mobile" dateTime={item.EventDateTime} />
                      <SCardContent style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Stack spacing={2} direction={'column'} style={{ flexGrow: '1' }}>
                          {!!getVenueLogo(item.VenueId) && (
                            <SVenueImageContainer>
                              <Image
                                src={getVenueLogo(item.VenueId)}
                                alt="Venue logo"
                                style={{ objectFit: 'scale-down', maxWidth: '125px' }}
                                sizes="125px"
                                fill
                              />
                            </SVenueImageContainer>
                          )}
                          <EventName eventName={item.EventName} />
                          <STimeAndCommentContainer>
                            <Typography
                              variant="button"
                              fontWeight={600}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {dayjs(item.EventDateTime).format('h:mm A')}
                            </Typography>
                            {item.Comments && <EventComment comment={item.Comments} />}
                          </STimeAndCommentContainer>
                        </Stack>
                      </SCardContent>
                      <SCardActions>
                        <ButtonSecondary
                          onClick={() => setCurrentEventId(item.EventId)}
                          variant="outlined"
                        >
                          PRICING
                        </ButtonSecondary>
                        <ButtonPrimary
                          disabled={item.Status === 'cancelled'}
                          onClick={() => router.push(`/tickets?eventid=${item.EventId}`)}
                          variant="contained"
                        >
                          Get TICKETS
                        </ButtonPrimary>
                      </SCardActions>
                    </SEventItem>
                    <VerticalDrawer
                      isOpen={currentEventId === item.EventId}
                      setIsClosed={() => setCurrentEventId('')}
                      anchor="right"
                      subtitle="Event Pricing for"
                      title={item.EventName}
                      footer={
                        <>
                          <ButtonSecondary
                            mode="light"
                            style={{ marginRight: `${constants.globalBufferSmall}` }}
                            onClick={() => setCurrentEventId('')}
                            fullWidth
                          >
                            Close
                          </ButtonSecondary>
                          <ButtonPrimary
                            disabled={item.Status === 'cancelled'}
                            onClick={() => router.push(`/tickets?eventid=${item.EventId}`)}
                            variant="contained"
                          >
                            Get TICKETS
                          </ButtonPrimary>
                        </>
                      }
                    >
                      <PricingView eventId={item.EventId} />
                    </VerticalDrawer>
                  </SEventWrapper>
                </div>
              ))}
              {!!currentEvents.totalCount &&
                dataPagesLoaded * PAGE_SIZE < Number(currentEvents.totalCount) && (
                  <LoadMoreSection>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={onLoadMore}
                      size="large"
                      sx={{ height: '45px' }}
                      fullWidth
                    >
                      <div style={{ fontWeight: '500', color: 'black' }}>
                        {isLoadingMoreEvents ? (
                          <CircularProgress color="inherit" />
                        ) : (
                          'Show more results'
                        )}
                      </div>
                    </Button>
                  </LoadMoreSection>
                )}
              <BackToTop />
            </Stack>
          ) : (
            <></>
          )}
          {!!currentEvents.isError ? (
            <GenericError />
          ) : !!currentEvents.data && currentEvents.data.length === 0 && !currentEvents.isLoading ? (
            <Stack spacing={3}>
              <PageHeader title="Events" style={{ paddingBottom: 0, paddingTop: 0 }} />
              <SItem>
                <SItemDetail>
                  <DefaultResponse>No results found</DefaultResponse>
                </SItemDetail>
              </SItem>
            </Stack>
          ) : (
            <></>
          )}
        </SEventsSection>
      </Container>
    </>
  )
}

const DateContainer = ({ mode, dateTime }: { mode: 'mobile' | 'desktop'; dateTime: string }) => {
  const Wrapper = mode === 'mobile' ? SDateContainerMobile : SDateContainerDesktop
  return (
    <Wrapper>
      <SDateText variant="button" fontWeight={600} fontSize="12px">
        <span>{dayjs(dateTime).format('ddd')}</span>
        <SDateDayDisplay>{dayjs(dateTime).format('DD')}</SDateDayDisplay>
        <span>{dayjs(dateTime).format("MMM 'YY")}</span>
      </SDateText>
    </Wrapper>
  )
}
